import React, { useEffect, useRef, useState } from 'react';
import { completeSignup, initiateSignup, requestSignupOtp } from '../services/auth.service';
import ButtonBusyLabel from './ButtonBusyLabel';
import MfaOtpCodeSlots, { MFA_OTP_SLOT_COUNT } from '../shared/components/MfaOtpCodeSlots';

function friendlySignupError(message) {
  return String(message || 'Something went wrong. Please try again.')
    .replace(/^(?:email|password2|password|name|organization|mobile_number|referral_code|code|challenge_id|channel):\s*/i, '')
    .trim();
}

/** Partner referral links look like /login?ref=CODE (or ?referral=CODE). */
function referralCodeFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return String(params.get('ref') || params.get('referral') || '').trim().slice(0, 64);
  } catch {
    return '';
  }
}

/** Two-step signup: profile details, then the emailed or texted code. */
export default function LoginSignupDialog({
  open,
  initialEmail = '',
  pendingEmail = '',
  onClose,
  onAuthenticated,
}) {
  const [phase, setPhase] = useState('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [channel, setChannel] = useState('email');
  const [channelOptions, setChannelOptions] = useState(['email']);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const firstFieldRef = useRef(null);
  const signupOtpInputId = 'login-signup-otp';

  useEffect(() => {
    if (!open) return undefined;
    setPhase(pendingEmail ? 'verify' : 'details');
    setName('');
    setEmail(String(pendingEmail || initialEmail || '').trim());
    setOrganization('');
    setMobileNumber('');
    setReferralCode(referralCodeFromUrl());
    setPassword('');
    setPassword2('');
    setCode('');
    setChallengeId('');
    setChannel('email');
    setChannelOptions(['email']);
    setError('');
    setSubmitting(false);
    setCooldownSeconds(0);
    return undefined;
  }, [open, initialEmail, pendingEmail]);

  useEffect(() => {
    if (!open || cooldownSeconds <= 0) return undefined;
    const intervalId = window.setInterval(() => {
      setCooldownSeconds((current) => (current > 1 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [open, cooldownSeconds]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  useEffect(() => {
    if (!open || pendingEmail || phase !== 'details') return undefined;
    const timer = window.setTimeout(() => firstFieldRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, pendingEmail, phase]);

  useEffect(() => {
    if (!open || !pendingEmail) return undefined;
    let cancelled = false;
    (async () => {
      setSubmitting(true);
      setError('');
      try {
        const result = await requestSignupOtp({ email: pendingEmail, channel: 'email' });
        if (cancelled) return;
        setChallengeId(String(result?.challenge_id || ''));
        setChannel(result?.channel || 'email');
        setPhase('verify');
      } catch (err) {
        if (cancelled) return;
        setError(friendlySignupError(err?.message));
        if (err?.retryAfterSeconds) setCooldownSeconds(err.retryAfterSeconds);
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, pendingEmail]);

  useEffect(() => {
    if (!open || phase !== 'verify') return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(signupOtpInputId)?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open, phase, challengeId]);

  if (!open) return null;

  const applyChallenge = (result, nextChannel) => {
    setChallengeId(String(result?.challenge_id || ''));
    setChannel(result?.channel || nextChannel || 'email');
    setCode('');
    setPhase('verify');
  };

  const handleDetailsSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError('');
    if (password !== password2) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await initiateSignup({
        name,
        email,
        password,
        password2,
        organization,
        timezone: 'Australia/Melbourne',
        mobileNumber,
        referralCode,
      });
      const options = Array.isArray(result?.otp_channel_options) && result.otp_channel_options.length
        ? result.otp_channel_options
        : ['email'];
      setChannelOptions(options);
      if (result?.auto_otp_sent && result?.challenge_id) {
        applyChallenge(result, result.channel || 'email');
        return;
      }
      if (options.length > 1) {
        setPhase('channel');
        return;
      }
      const otp = await requestSignupOtp({ email, channel: options[0] || 'email' });
      applyChallenge(otp, options[0] || 'email');
    } catch (err) {
      setError(friendlySignupError(err?.message));
      if (err?.retryAfterSeconds) setCooldownSeconds(err.retryAfterSeconds);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChannel = async (nextChannel) => {
    if (submitting || cooldownSeconds > 0) return;
    setError('');
    setSubmitting(true);
    try {
      const result = await requestSignupOtp({ email, channel: nextChannel });
      applyChallenge(result, nextChannel);
    } catch (err) {
      setError(friendlySignupError(err?.message));
      if (err?.retryAfterSeconds) setCooldownSeconds(err.retryAfterSeconds);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError('');
    if (!challengeId) {
      setError('Request a new verification code, then try again.');
      return;
    }
    const otpDigits = String(code || '').replace(/\D/g, '');
    if (otpDigits.length !== MFA_OTP_SLOT_COUNT) {
      setError(`Enter the ${MFA_OTP_SLOT_COUNT}-digit verification code.`);
      return;
    }
    setSubmitting(true);
    try {
      const result = await completeSignup({ email, challengeId, code: otpDigits });
      if (!result?.access) {
        setError('Account created, but sign-in did not return a session. Sign in with your new password.');
        return;
      }
      onAuthenticated(result);
    } catch (err) {
      setError(friendlySignupError(err?.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (submitting || cooldownSeconds > 0) return;
    setError('');
    setSubmitting(true);
    try {
      const result = await requestSignupOtp({ email, channel });
      setChallengeId(String(result?.challenge_id || ''));
      setCode('');
    } catch (err) {
      setError(friendlySignupError(err?.message));
      if (err?.retryAfterSeconds) setCooldownSeconds(err.retryAfterSeconds);
    } finally {
      setSubmitting(false);
    }
  };

  const title = phase === 'verify' ? 'Verify your account' : phase === 'channel' ? 'Choose how to verify' : 'Create account';
  const destination = channel === 'sms' ? 'your phone' : email || 'your email';
  const otpDigits = String(code || '').replace(/\D/g, '');
  const canVerify = Boolean(challengeId) && otpDigits.length === MFA_OTP_SLOT_COUNT;
  const isVerifyPhase = phase === 'verify';

  return (
    <div
      className="login-forgot-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div
        className={[
          'login-forgot-dialog',
          'login-signup-dialog',
          isVerifyPhase ? 'login-mfa-dialog' : null,
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-signup-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div
          className={[
            'login-forgot-dialog-head',
            isVerifyPhase ? 'login-mfa-dialog-head' : null,
          ].filter(Boolean).join(' ')}
        >
          <h2 id="login-signup-title" className="login-forgot-title">
            {title}
          </h2>
          <button
            type="button"
            className="login-forgot-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {phase === 'details' ? (
          <form className="login-forgot-body" onSubmit={handleDetailsSubmit} noValidate>
            <p className="login-forgot-hint">
              Create your organization account. We will email a code to confirm it is yours.
            </p>
            <div className="login-forgot-field">
              <input
                ref={firstFieldRef}
                className="login-forgot-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Full name"
                aria-label="Full name"
                autoComplete="name"
                required
              />
            </div>
            <div className="login-forgot-field">
              <input
                className="login-forgot-input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Work email"
                aria-label="Work email"
                autoComplete="email"
                required
              />
            </div>
            <div className="login-forgot-field">
              <input
                className="login-forgot-input"
                value={organization}
                onChange={(event) => setOrganization(event.target.value)}
                placeholder="Organization"
                aria-label="Organization"
                autoComplete="organization"
                required
              />
            </div>
            <div className="login-forgot-field">
              <input
                className="login-forgot-input"
                value={mobileNumber}
                onChange={(event) => setMobileNumber(event.target.value)}
                placeholder="Mobile number (optional)"
                aria-label="Mobile number"
                autoComplete="tel"
              />
            </div>
            <div className="login-forgot-field">
              <input
                className="login-forgot-input"
                value={referralCode}
                onChange={(event) => setReferralCode(event.target.value)}
                placeholder="Partner referral code (optional)"
                aria-label="Partner referral code"
                autoComplete="off"
                maxLength={64}
              />
            </div>
            <div className="login-forgot-field">
              <input
                className="login-forgot-input"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
                aria-label="Password"
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div className="login-forgot-field">
              <input
                className="login-forgot-input"
                type="password"
                value={password2}
                onChange={(event) => setPassword2(event.target.value)}
                placeholder="Confirm password"
                aria-label="Confirm password"
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            {error ? (
              <div className="login-forgot-error" role="alert">
                {error}
              </div>
            ) : null}
            <div className="login-forgot-actions login-forgot-actions--single">
              <button type="submit" className="login-forgot-submit-btn" disabled={submitting}>
                {submitting ? <ButtonBusyLabel>Creating account…</ButtonBusyLabel> : 'Create account'}
              </button>
            </div>
          </form>
        ) : null}

        {phase === 'channel' ? (
          <div className="login-forgot-body">
            <p className="login-forgot-hint">
              Choose where to send the verification code for {email}.
            </p>
            {error ? (
              <div className="login-forgot-error" role="alert">
                {error}
              </div>
            ) : null}
            <div className="login-forgot-actions login-forgot-actions--stacked">
              {channelOptions.includes('email') ? (
                <button
                  type="button"
                  className="login-forgot-submit-btn"
                  onClick={() => handleChannel('email')}
                  disabled={submitting}
                >
                  Email
                </button>
              ) : null}
              {channelOptions.includes('sms') ? (
                <button
                  type="button"
                  className="login-forgot-btn login-forgot-resend-btn"
                  onClick={() => handleChannel('sms')}
                  disabled={submitting}
                >
                  Text message
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {phase === 'verify' ? (
          <form className="login-forgot-body login-mfa-dialog-body" onSubmit={handleVerify} noValidate>
            <p className="login-forgot-hint">
              {submitting && !challengeId
                ? 'Sending a verification code…'
                : (
                  <>
                    Enter the {MFA_OTP_SLOT_COUNT}-digit verification code sent to {destination}.
                  </>
                )}
            </p>
            <div className="login-forgot-field">
              <MfaOtpCodeSlots
                id={signupOtpInputId}
                value={code}
                onChange={setCode}
                disabled={submitting || !challengeId}
                aria-label={`${MFA_OTP_SLOT_COUNT}-digit verification code`}
              />
            </div>
            <div className="login-mfa-secondary-actions">
              <button
                type="button"
                className="login-forgot-btn"
                onClick={handleResend}
                disabled={submitting || cooldownSeconds > 0 || !email}
              >
                {cooldownSeconds > 0 ? `Resend available in ${cooldownSeconds}s` : 'Resend code'}
              </button>
            </div>
            {error ? (
              <div className="login-forgot-error" role="alert">
                {error}
              </div>
            ) : null}
            <div className="login-forgot-actions login-forgot-actions--single">
              <button
                type="submit"
                className="login-forgot-submit-btn login-mfa-verify-btn"
                disabled={submitting || !canVerify}
              >
                {submitting ? <ButtonBusyLabel>Verifying…</ButtonBusyLabel> : 'Verify and sign in'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}
